import React, { useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLayout } from '../contexts/LayoutContext';
import { modules } from '../config/modules';
import { LayoutDashboard, ChevronRight, ChevronDown, Box, Landmark } from 'lucide-react';
import './Navigation.css';

export default function Sidebar({ isOpen, onClose }) {
    const { user, logout, hasPermission } = useAuth();
    const { addTab, coopName } = useLayout();
    const location = useLocation();
    const navigate = useNavigate();
    const [expandedMenu, setExpandedMenu] = useState(null);

    const toggleAccordion = (menuTitle) => {
        setExpandedMenu(prev => prev === menuTitle ? null : menuTitle);
    };

    const handleSubItemClick = (subItem) => {
        if (!subItem) return;
        addTab({ path: subItem.href, title: subItem.title });
        navigate(subItem.href);
        if (window.innerWidth < 768) {
            onClose();
        }
    };

    const currentModule = modules.find(m => location.pathname.startsWith(m.href));

    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 md:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar container */}
            <div className={`fixed inset-y-0 left-0 z-30 w-72 bg-white transform transition-transform duration-300 ease-in-out md:translate-x-0 overflow-y-auto ${isOpen ? 'translate-x-0' : '-translate-x-full'} border-r border-gray-100`}>
                <div className="flex flex-col min-h-full">
                    {/* Logo Area (Simulated same as Navbar) */}
                    <div className="flex items-center h-20 px-8 border-b border-gray-50 bg-gray-50/20">
                        <Link to="/" className="flex items-center space-x-3 group">
                            <div className="bg-uni-green p-1.5 rounded-lg">
                                <Landmark className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-base font-black tracking-tighter text-uni-green uppercase">
                                {coopName || 'Universitaria'}
                            </span>
                        </Link>
                    </div>

                    {/* Navigation Items */}
                    <div className="flex-1 overflow-y-auto pt-10 pb-20 px-6 space-y-2 font-sans">
                        <NavLink
                            to="/"
                            end
                            onClick={onClose}
                            className={({ isActive }) =>
                                `group flex items-center px-4 py-3 text-xs font-black uppercase tracking-widest rounded-2xl transition-all duration-200 ${isActive
                                    ? 'bg-uni-green text-white shadow-lg shadow-uni-green/20'
                                    : 'text-slate-400 hover:bg-gray-50 hover:text-uni-green'
                                }`
                            }
                        >
                            <LayoutDashboard className={`w-4 h-4 mr-3`} />
                            <span className="flex-1">Dashboard</span>
                        </NavLink>

                        <div className="pt-10 pb-3 px-4">
                            <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.3em]">
                                Módulos
                            </p>
                        </div>

                        {modules.map((module) => {
                            if (module.permission && !hasPermission(module.permission)) return null;
                            const isExpanded = expandedMenu === module.title;
                            const isActiveModule = location.pathname.startsWith(module.href) && module.href !== '/';

                            return (
                                <div key={module.title} className="space-y-1">
                                    <button
                                        onClick={() => module.subItems ? toggleAccordion(module.title) : navigate(module.href)}
                                        className={`w-full group flex items-center px-4 py-3 text-xs font-black uppercase tracking-widest rounded-2xl transition-all duration-200 ${isActiveModule || isExpanded
                                            ? 'bg-uni-green/5 text-uni-green'
                                            : 'text-slate-500 hover:bg-gray-50 hover:text-uni-green'
                                            }`}
                                    >
                                        <module.icon className={`w-4 h-4 mr-3 transition-colors ${isActiveModule || isExpanded ? 'text-uni-green' : 'text-slate-300 group-hover:text-uni-green'}`} />
                                        <span className="flex-1 text-left">{module.title}</span>
                                        {module.subItems && (
                                            <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                                                <ChevronDown className={`w-4 h-4 ${isActiveModule || isExpanded ? 'text-uni-green' : 'text-slate-300'}`} />
                                            </div>
                                        )}
                                    </button>

                                    {/* Sub Items (Accordion Content) */}
                                    {module.subItems && isExpanded && (
                                        <div className="ml-6 pl-4 border-l-2 border-uni-green/10 space-y-1 py-1">
                                            {module.subItems.map(sub => {
                                                if (sub.permission && !hasPermission(sub.permission)) return null;
                                                const isActiveSub = location.pathname === sub.href;
                                                return (
                                                    <button
                                                        key={sub.title}
                                                        onClick={() => handleSubItemClick(sub)}
                                                        className={`w-full flex items-center px-4 py-2.5 text-[11px] font-bold rounded-xl transition-all duration-200 ${isActiveSub
                                                            ? 'text-uni-green bg-white shadow-sm border border-gray-100'
                                                            : 'text-slate-400 hover:text-uni-green hover:bg-white'
                                                            }`}
                                                    >
                                                        {sub.icon && <sub.icon className="w-3.5 h-3.5 mr-3" />}
                                                        <span>{sub.title}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    {/* Debug Info (Opcional, ayuda a diagnosticar permisos) */}
                    <div className="mt-auto p-4 border-t border-gray-100 bg-gray-50/50">
                        <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">Mi Perfil Técnico</p>
                        <div className="space-y-1">
                            <div className="flex flex-wrap gap-1">
                                {user?.roles?.map(r => (
                                    <span key={r} className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-[9px] font-bold">{r}</span>
                                ))}
                            </div>
                            <div className="text-[8px] text-slate-400 truncate">
                                {user?.permisos?.length} permisos cargados
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </>
    );
}
